export const EXAM_REQUEST_TIMEOUT_MS = 20_000;

export type ExamRequestFailure = {
  reason: 'timeout' | 'aborted' | 'busy' | 'request';
  code?: string | number;
  httpStatus?: number;
};

type RequestError = Error & {code?: string | number;};

const createRequestError = (message: string, code: 'EXAM_TIMEOUT' | 'EXAM_ABORTED'): RequestError => {
  const error = new Error(message) as RequestError;
  error.code = code;
  return error;
};

const asSignals = (signal?: AbortSignal | readonly AbortSignal[]) => {
  if (!signal) return [];
  return (Array.isArray(signal) ? signal : [signal]).filter((item): item is AbortSignal => Boolean(item));
};

export async function settleExamRequest<T>(
  work: (signal: AbortSignal) => Promise<T>,
  timeoutMs = EXAM_REQUEST_TIMEOUT_MS,
  signal?: AbortSignal | readonly AbortSignal[],
): Promise<T> {
  const signals = asSignals(signal);
  if (signals.some(item => item.aborted)) {
    throw createRequestError('exam request was aborted', 'EXAM_ABORTED');
  }

  const request = new AbortController();
  let interrupt!: (error: Error) => void;
  const interrupted = new Promise<never>((_resolve, reject) => { interrupt = reject; });
  const abort = () => {
    interrupt(createRequestError('exam request was aborted', 'EXAM_ABORTED'));
    request.abort();
  };
  const timer = setTimeout(() => {
    interrupt(createRequestError('exam request timed out', 'EXAM_TIMEOUT'));
    request.abort();
  }, timeoutMs);

  signals.forEach(item => item.addEventListener('abort', abort, {once: true}));
  try {
    let workPromise: Promise<T>;
    try {
      workPromise = work(request.signal);
    } catch (error) {
      workPromise = Promise.reject(error);
    }
    return await Promise.race([workPromise, interrupted]);
  } finally {
    clearTimeout(timer);
    signals.forEach(item => item.removeEventListener('abort', abort));
  }
}

const isSafeCode = (value: unknown): value is string | number => typeof value === 'string' || typeof value === 'number';

export function getExamRequestFailure(error: unknown): ExamRequestFailure {
  const source = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const code = isSafeCode(source.code) ? source.code : undefined;
  const response = source.response && typeof source.response === 'object' ? source.response as Record<string, unknown> : undefined;
  const httpStatus = typeof source.httpStatus === 'number'
    ? source.httpStatus
    : typeof source.status === 'number'
      ? source.status
      : typeof response?.status === 'number'
        ? response.status
        : undefined;

  if (source.reason === 'timeout' || source.reason === 'aborted' || source.reason === 'busy' || source.reason === 'request') {
    return httpStatus === undefined ? {reason: source.reason, code} : {reason: source.reason, code, httpStatus};
  }

  if (code === 'EXAM_TIMEOUT' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return {reason: 'timeout', code};
  if (code === 'EXAM_ABORTED' || code === 'ERR_CANCELED' || source.name === 'AbortError') {
    return {reason: 'aborted', code: code === 'ERR_CANCELED' ? code : 'EXAM_ABORTED'};
  }
  if (source.name === 'ConditionError') return {reason: 'busy', code};
  return {reason: 'request', code, httpStatus};
}
